import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  ScheduleChangeUserActionEnum,
  type TimeGroupingEnum,
} from "@b2bportal/core-types";
import {
  useFlightScheduleChangeForm,
  ScheduleChangeTimePreferences,
} from "@tenant/flights";
import { Button, ButtonTypeEnum, RadioGroup } from "@tenant/ui";
import clsx from "clsx";

import defaultStyles from "./FlightScheduleChangeForm.module.scss";

export interface IFlightScheduleChangeFormProps {
  hoursToDeparture?: number;
  onSubmit: (
    scheduleChangeUserAction: ScheduleChangeUserActionEnum | null,
    outboundTimes: TimeGroupingEnum[],
    inboundTimes: TimeGroupingEnum[]
  ) => void;
  outboundChanged: boolean;
  outboundDepartureDate?: string;
  returnChanged: boolean;
  returnDepartureDate?: string;
}

export const FlightScheduleChangeForm = (
  props: IFlightScheduleChangeFormProps
) => {
  const { onSubmit, outboundChanged, returnChanged } = props;
  const COMPONENT_KEY = CoreFlightsComponent.FlightScheduleChangeForm;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { context, handlers, text } = useFlightScheduleChangeForm(props);

  return (
    <div className={clsx(block)}>
      <span className={cn("form-section-title")}>{text.headingCopy}</span>
      <RadioGroup
        className={cn("form-radios")}
        defaultValue={ScheduleChangeUserActionEnum.Accept}
        onSelectValue={handlers.onUserActionChange}
        options={[
          {
            className: cn("form-radio-button", ["accept"]),
            label: (
              <>
                <span className={cn("radio-title")}>
                  {text.acceptButtonTitle}
                </span>
                <span className={cn("radio-details")}>
                  {text.acceptDetails}
                </span>
              </>
            ),
            key: ScheduleChangeUserActionEnum.Accept,
            value: ScheduleChangeUserActionEnum.Accept,
          },
          {
            className: cn("form-radio-button", ["deny"]),
            label: (
              <>
                <span className={cn("radio-title")}>
                  {text.denyButtonTitle}
                </span>
                <span className={cn("radio-details")}>{text.denyDetails}</span>
              </>
            ),
            key: ScheduleChangeUserActionEnum.Deny,
            value: ScheduleChangeUserActionEnum.Deny,
          },
        ]}
        selectedOption={context.userAction}
      />
      {context.hasRejected && (
        <div className={cn("deny-schedule-change-form")}>
          {outboundChanged && (
            <div className={cn("time-preference-selection")}>
              <div className={cn("form-section-title")}>
                {text.outboundTimePreferenceCopy}
              </div>
              <div className={cn("time-preference-row")}>
                <ScheduleChangeTimePreferences
                  selectedTimes={context.outboundTimes}
                  setSelected={handlers.setOutboundTimes}
                />
              </div>
            </div>
          )}
          {returnChanged && (
            <div className={cn("time-preference-selection")}>
              <span className={cn("form-section-title")}>
                {text.returnTimePreferenceCopy}
              </span>
              <div className="time-preference-row">
                <ScheduleChangeTimePreferences
                  selectedTimes={context.returnTimes}
                  setSelected={handlers.setReturnTimes}
                />
              </div>
            </div>
          )}
        </div>
      )}
      <Button
        buttonType={ButtonTypeEnum.Primary}
        disabled={context.continueDisabled}
        onClick={() =>
          onSubmit(
            context.userAction,
            context.outboundTimes,
            context.returnTimes
          )
        }
      >
        {text.submitButtonLabel}
      </Button>
    </div>
  );
};
